// Camada de validação de posições GPS, usada pelo Centro de Histórico antes
// de qualquer renderização (mapa/trajeto/player) ou cálculo (tabela,
// exportações CSV/Excel/PDF). Existe porque o Traccar às vezes devolve
// fixes de GPS claramente inválidos (0,0; fora do intervalo geográfico;
// NaN/Infinity vindo de um sensor com erro) e, mais raramente, um "salto"
// impossível entre dois pontos consecutivos (ex.: Porto Alegre -> costa da
// África em poucos minutos) — nenhum dos dois pode virar linha no mapa nem
// entrar em nenhuma estatística.

export interface GeoFix {
  lat: number;
  lng: number;
  timestamp: string;
}

// Teto de velocidade fisicamente plausível para um veículo terrestre,
// generoso o bastante para não descartar picos reais (rodovia + folga do
// GPS/relógio do rastreador), mas baixo o bastante para pegar qualquer
// teleporte entre continentes/estados em poucos minutos.
export const MAX_PLAUSIBLE_SPEED_KMH = 300;

// Abaixo desse deslocamento, nem calculamos velocidade implícita — GPS
// parado naturalmente "tremula" alguns metros entre fixes, e dividir uma
// distância minúscula por um intervalo de tempo minúsculo produz uma
// velocidade absurda sem que exista salto nenhum de verdade.
export const JUMP_CHECK_MIN_DISTANCE_KM = 0.3;

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

export function isValidLat(lat: unknown): lat is number {
  return isFiniteNumber(lat) && lat >= -90 && lat <= 90;
}

export function isValidLng(lng: unknown): lng is number {
  return isFiniteNumber(lng) && lng >= -180 && lng <= 180;
}

// Lat/lng exatamente 0,0 é o valor de fix "vazio" mais comum em
// rastreadores GPS (sem sinal ainda) — quase nunca é uma posição real
// (ponto no meio do Golfo da Guiné), então tratamos como inválido.
export function isZeroZero(lat: number, lng: number): boolean {
  return Math.abs(lat) < 1e-9 && Math.abs(lng) < 1e-9;
}

export function isValidCoordinate(lat: unknown, lng: unknown): lat is number {
  if (!isValidLat(lat) || !isValidLng(lng as unknown)) return false;
  if (isZeroZero(lat as number, lng as number)) return false;
  return true;
}

// Haversine — distância em km entre dois pontos lat/lng.
export function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// true quando o deslocamento entre "from" e "to" implica uma velocidade
// fisicamente impossível para o intervalo de tempo entre eles.
export function isImpossibleJump(from: GeoFix, to: GeoFix): boolean {
  const distKm = haversineKm(from, to);
  if (distKm < JUMP_CHECK_MIN_DISTANCE_KM) return false;
  const dtHours = (new Date(to.timestamp).getTime() - new Date(from.timestamp).getTime()) / 3_600_000;
  if (dtHours <= 0) return true; // "andou" sem tempo decorrido (ou tempo regressivo)
  return distKm / dtHours > MAX_PLAUSIBLE_SPEED_KMH;
}

export interface FilterResult<T> {
  valid: T[];
  discarded: number;
}

// Filtra uma sequência ordenada por tempo de posições, removendo:
//   1) coordenadas geometricamente inválidas (fora de intervalo, 0/0,
//      NaN/Infinity, vazias/nulas — cobertas por isValidCoordinate);
//   2) saltos impossíveis — comparados sempre contra o último ponto VÁLIDO
//      aceito (não contra o ponto bruto anterior), então uma sequência de
//      vários fixes ruins seguidos é descartada inteira até o trajeto
//      voltar a ser plausível, sem nunca desenhar/contar um salto.
// Como cada ponto aceito já é, por construção, plausível em relação ao
// anterior aceito, o array resultante nunca precisa de "quebra de linha"
// separada: basta desenhar uma polyline contínua com `valid`.
export function filterValidPositions<T extends GeoFix>(positions: T[]): FilterResult<T> {
  const valid: T[] = [];
  let discarded = 0;
  let last: T | null = null;
  for (const p of positions) {
    if (!isValidCoordinate(p.lat, p.lng)) {
      discarded++;
      continue;
    }
    if (last && isImpossibleJump(last, p)) {
      discarded++;
      continue;
    }
    valid.push(p);
    last = p;
  }
  return { valid, discarded };
}
